import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantAreas } from '../lib/access/useTenantAreas';
import { agentClient } from '../lib/connect/clients';
export const MEMORIES_KEY = 'memories';
export const TEAM_MEMORIES_KEY = 'team-memories';
function toRow(m) {
    if (!m.id) return null;
    return {
        id: m.id,
        kind: m.kind ?? '',
        title: m.title ?? '',
        body: m.body ?? '',
        scopeKind: m.scopeKind ?? '',
        scopeId: m.scopeId ?? '',
        updatedBy: m.updatedBy ?? '',
        updatedAt: m.updatedAt?.toDate?.(),
        status: m.status || 'active',
        decisionNote: m.decisionNote ?? ''
    };
}
async function fetchMemories(scopes) {
    const seen = new Set();
    const rows = [];
    const personal = await agentClient.listMemories({});
    for (const m of personal.memories ?? []){
        const row = toRow(m);
        if (row && !seen.has(row.id)) {
            seen.add(row.id);
            rows.push(row);
        }
    }
    for (const scope of scopes){
        if (!scope.scopeKind || !scope.scopeId) continue;
        const resp = await agentClient.listMemories({
            scopeKind: scope.scopeKind,
            scopeId: scope.scopeId
        });
        for (const m of resp.memories ?? []){
            if (m.kind !== 'project') continue;
            const row = toRow(m);
            if (row && !seen.has(row.id)) {
                seen.add(row.id);
                rows.push(row);
            }
        }
    }
    rows.sort((a, b)=>(b.updatedAt?.getTime() ?? 0) - (a.updatedAt?.getTime() ?? 0));
    return rows;
}
export function useMemories(scopes) {
    const scopeKey = scopes.map((s)=>`${s.scopeKind ?? ''}:${s.scopeId ?? ''}`).join('|');
    return useQuery({
        queryKey: [
            MEMORIES_KEY,
            scopeKey
        ],
        queryFn: ()=>fetchMemories(scopes)
    });
}
export function useUpsertMemory(scopes) {
    const qc = useQueryClient();
    const scopeKey = scopes.map((s)=>`${s.scopeKind ?? ''}:${s.scopeId ?? ''}`).join('|');
    return useMutation({
        mutationFn: async (input)=>agentClient.upsertMemory(input),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    MEMORIES_KEY,
                    scopeKey
                ]
            })
    });
}
export function deleteMemory(ref) {
    const scoped = ref.scopeKind && ref.scopeKind !== 'user';
    return agentClient.deleteMemory({
        id: ref.id,
        scopeKind: scoped ? ref.scopeKind : '',
        scopeId: scoped ? ref.scopeId : ''
    });
}
export function useDeleteMemory(_scopes) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (ref)=>deleteMemory(ref),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    MEMORIES_KEY
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    TEAM_MEMORIES_KEY
                ]
            });
        }
    });
}
async function fetchTeam(status) {
    const resp = await agentClient.listMemories({
        scopeKind: 'team',
        status
    });
    return (resp.memories ?? []).map(toRow).filter((r)=>r !== null && r.kind === 'team');
}
/** The team's approved notes: everyone reads them. */ export function useTeamMemories() {
    return useQuery({
        queryKey: [
            TEAM_MEMORIES_KEY,
            'active'
        ],
        queryFn: ()=>fetchTeam('active')
    });
}
/**
 * Team notes waiting for approval. Only admins and Company Settings admins may
 * list them (the server's rule), so everyone else never asks and the approvals
 * section stays hidden.
 */ export function usePendingTeamMemories() {
    const { canManageCompanySettings } = useTenantAreas();
    const query = useQuery({
        queryKey: [
            TEAM_MEMORIES_KEY,
            'pending'
        ],
        queryFn: ()=>fetchTeam('pending'),
        enabled: canManageCompanySettings
    });
    return {
        ...query,
        isAdmin: canManageCompanySettings
    };
}
export function useProposeTeamMemory() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>agentClient.upsertMemory({
                ...input,
                kind: 'team'
            }),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    TEAM_MEMORIES_KEY
                ]
            })
    });
}
export function useDecideTeamMemory() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>agentClient.decideTeamMemory(input),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    TEAM_MEMORIES_KEY
                ]
            })
    });
}
export function memoryChipLabel(memories) {
    const titles = memories.map((m)=>m.title.trim()).filter(Boolean).slice(0, 4);
    if (titles.length === 0) return '';
    return `Using: ${titles.join(' · ')}`;
}
